(() => {
  const SELECTORS = {
    video: '[data-hero-media-video]',
  };

  const DESKTOP_QUERY = '(min-width: 750px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const OBSERVER_THRESHOLD = 0.25;

  const desktopViewport = window.matchMedia(DESKTOP_QUERY);
  const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);

  /** @type {Set<HTMLVideoElement>} */
  const registeredVideos = new Set();

  /**
   * The desktop and the mobile file are held in data attributes so only the one
   * that matches the viewport is ever downloaded. Either attribute may be
   * missing, in which case the other one serves both breakpoints.
   *
   * @param {HTMLVideoElement} video
   * @returns {string}
   */
  const sourceForViewport = (video) => {
    const desktopSource = video.dataset.videoDesktop || '';
    const mobileSource = video.dataset.videoMobile || '';

    if (desktopViewport.matches) {
      return desktopSource || mobileSource;
    }

    return mobileSource || desktopSource;
  };

  /** @param {HTMLVideoElement} video */
  const requestPlay = (video) => {
    const playback = video.play();

    if (playback && typeof playback.catch === 'function') {
      playback.catch(() => {
        /* A browser that blocks autoplay leaves the poster image in place. */
      });
    }
  };

  /** @param {HTMLVideoElement} video */
  const loadAndPlay = (video) => {
    if (reducedMotion.matches) return;

    const source = sourceForViewport(video);
    if (!source) return;

    if (video.dataset.loadedSource === source) {
      requestPlay(video);
      return;
    }

    video.dataset.loadedSource = source;
    video.src = source;
    video.load();
    requestPlay(video);
  };

  /**
   * Nothing is fetched until the banner is close to the viewport. A Primary
   * media section can sit well down the page, and preload="none" alone would
   * not stop the src assignment from starting the download.
   */
  const intersectionObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = /** @type {HTMLVideoElement} */ (entry.target);

              if (entry.isIntersecting) {
                loadAndPlay(video);
                return;
              }

              if (video.dataset.loadedSource) video.pause();
            });
          },
          { threshold: OBSERVER_THRESHOLD }
        )
      : null;

  /** @param {HTMLVideoElement} video */
  const register = (video) => {
    if (registeredVideos.has(video)) return;
    registeredVideos.add(video);

    if (intersectionObserver) {
      intersectionObserver.observe(video);
      return;
    }

    loadAndPlay(video);
  };

  /** @param {HTMLVideoElement} video */
  const unregister = (video) => {
    registeredVideos.delete(video);
    if (intersectionObserver) intersectionObserver.unobserve(video);
  };

  /** @param {ParentNode} [root] */
  const registerWithin = (root) => {
    const scope = root || document;
    scope.querySelectorAll(SELECTORS.video).forEach((video) => {
      register(/** @type {HTMLVideoElement} */ (video));
    });
  };

  /** @param {ParentNode} root */
  const unregisterWithin = (root) => {
    root.querySelectorAll(SELECTORS.video).forEach((video) => {
      unregister(/** @type {HTMLVideoElement} */ (video));
    });
  };

  const handleBreakpointChange = () => {
    registeredVideos.forEach((video) => {
      const source = sourceForViewport(video);
      if (!source || source === video.dataset.loadedSource) return;
      if (!video.dataset.loadedSource) return;

      loadAndPlay(video);
    });
  };

  const handleReducedMotionChange = () => {
    if (reducedMotion.matches) {
      registeredVideos.forEach((video) => video.pause());
      return;
    }

    registeredVideos.forEach((video) => {
      if (video.dataset.loadedSource) requestPlay(video);
    });
  };

  desktopViewport.addEventListener('change', handleBreakpointChange);
  reducedMotion.addEventListener('change', handleReducedMotionChange);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => registerWithin(), { once: true });
  } else {
    registerWithin();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const target = /** @type {CustomEvent} */ (event).target;
    if (target instanceof HTMLElement) registerWithin(target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const target = /** @type {CustomEvent} */ (event).target;
    if (target instanceof HTMLElement) unregisterWithin(target);
  });
})();
